import { useTranslation } from "react-i18next";

import { LabSection } from "src/core/components/pdf/sections/Lab";
import { Block } from "src/core/components/pdf/shared/Block";
import { EIStakeholder } from "src/core/components/pdf/shared/EIStakeholder";
import { PdfFooter } from "src/core/components/pdf/shared/PdfFooter";
import { PdfHeader } from "src/core/components/pdf/shared/PdfHeader";
import { PdfPage } from "src/core/components/pdf/shared/PdfPage";
import { Stakeholder } from "src/core/components/pdf/shared/Stakeholder";
import { Title } from "src/core/components/pdf/shared/Title";
import { FolderFileType, FolderStakeholderType } from "src/core/components/pdf/shared/types";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { User } from "src/core/gql/graphql";

type StakeholdersSectionProps = {
  currentUser: User;
  folderFile: FolderFileType;
  sortedStakeholders: FolderStakeholderType[];
  activityWithRiskOptions: IRiskOption[];
  countryWithRiskOptions: IRiskOption[];
  legalTypesWithRiskOptions: IRiskOption[];
  legalStatusOptions: ISelectOption[];
  legalStatusPPHOptions: ISelectOption[];
};

export const StakeholdersSection = ({
  currentUser,
  folderFile,
  sortedStakeholders,
  activityWithRiskOptions,
  countryWithRiskOptions,
  legalTypesWithRiskOptions,
  legalStatusOptions,
  legalStatusPPHOptions,
}: StakeholdersSectionProps) => {
  const { t } = useTranslation();

  // Est-ce qu'il s'agit d'une Entreprise Individuelle (EI) ?
  const isEI = folderFile.fileCustomerFolder?.customer?.isPerson;
  const title = isEI
    ? t("pdf.sections.stakeholders.titlePerson")
    : t("pdf.sections.stakeholders.titleCorporation");

  const stakeholdersPerPage = 2;
  const totalStakeholders = sortedStakeholders.length;
  const totalPages = Math.ceil(totalStakeholders / stakeholdersPerPage);
  const isOdd = totalStakeholders % 2 !== 0;

  return Array.from({ length: totalPages }, (_, pageIndex) => {
    const startIndex = pageIndex * stakeholdersPerPage;
    const endIndex = Math.min(startIndex + stakeholdersPerPage, totalStakeholders);
    const pageStakeholders = sortedStakeholders.slice(startIndex, endIndex);
    const isLastPage = pageIndex === totalPages - 1;
    const shouldShowLab = isLastPage && isOdd;

    return (
      <PdfPage key={pageIndex}>
        <PdfHeader />
        {/* Section Personnes */}
        <Block>
          <Title title={title} />

          {pageStakeholders.map((stakeholder) =>
            isEI ? (
              <EIStakeholder
                key={stakeholder.entity.id}
                stakeholder={stakeholder}
                activityWithRiskOptions={activityWithRiskOptions}
                countryWithRiskOptions={countryWithRiskOptions}
                legalStatusPPHOptions={legalStatusPPHOptions}
              />
            ) : (
              <Stakeholder
                key={stakeholder.entity.id}
                stakeholder={stakeholder}
                activityWithRiskOptions={activityWithRiskOptions}
                countryWithRiskOptions={countryWithRiskOptions}
                legalTypesWithRiskOptions={legalTypesWithRiskOptions}
                legalStatusOptions={legalStatusOptions}
              />
            )
          )}
        </Block>

        {/* Section LAB - affichée uniquement sur la dernière page si nombre impair */}
        {shouldShowLab && !isEI && <LabSection folderFile={folderFile} />}

        <PdfFooter folderFile={folderFile} currentUser={currentUser} />
      </PdfPage>
    );
  });
};
